// Admin Panel - Organizer controls
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import { Button, Input, Card, Modal, IconPicker, ColorPicker } from '../components';
import HamburgerMenu from '../components/HamburgerMenu';
import {
  createCategory,
  updateCategory,
  createGame,
  updateCompetition,
  updateCompetitionStatus,
  regenerateInviteCode,
  updatePlayerStatus,
  resetPlayerPin,
  deleteCompetition
} from '../services';
import { deletePendingGame, fixPlayingGamesStatus } from '../services/gameService';
import { getCompetitionTokens } from '../services/tokenService';
import { getGameSectionsForLeague, getUniqueCategoriesForLeague } from '../services/gameSectionService';
import { generatePin } from '../utils/crypto';
import { getTodayDateString } from '../utils/dateHelpers';
import { addRankingMethodToCompetition } from '../utils/addRankingMethodToCompetition';
import { generateTimeOptions } from '../utils/timeHelpers';

const AdminPanel = () => {
  const { competitionId } = useParams();
  const navigate = useNavigate();
  const { competition, players, categories, games, isOrganizer, refreshCompetition } = useCompetition();

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showGameModal, setShowGameModal] = useState(false);
  const [showPlayerModal, setShowPlayerModal] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);

  // Category form
  const [categoryName, setCategoryName] = useState('');
  const [categoryIcon, setCategoryIcon] = useState('🎲');
  const [categoryColor, setCategoryColor] = useState('#3B82F6');
  const [categoryError, setCategoryError] = useState('');
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryMode, setCategoryMode] = useState('new'); // 'new' or 'existing'
  const [existingCategories, setExistingCategories] = useState([]);
  const [loadingExistingCategories, setLoadingExistingCategories] = useState(false);
  const [selectedExistingCategory, setSelectedExistingCategory] = useState('');

  // Game form
  const [gameName, setGameName] = useState('');
  const [gameCategory, setGameCategory] = useState('');
  const [gameDate, setGameDate] = useState(getTodayDateString());
  const [gameTime, setGameTime] = useState('');
  const [gameNotes, setGameNotes] = useState('');
  const [gameKnockoutMode, setGameKnockoutMode] = useState(false);
  const [gameError, setGameError] = useState('');
  const [gameSaving, setGameSaving] = useState(false);

  const [newPin, setNewPin] = useState('');
  const [success, setSuccess] = useState('');
  const [showDebugModal, setShowDebugModal] = useState(false);
  const [debugTokens, setDebugTokens] = useState([]);
  const [loadingDebug, setLoadingDebug] = useState(false);
  const [editingRankingMethod, setEditingRankingMethod] = useState(false);

  // Library game modal
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [libraryGames, setLibraryGames] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [selectedLibraryGame, setSelectedLibraryGame] = useState(null);
  const [libraryGameDate, setLibraryGameDate] = useState(getTodayDateString());
  const [libraryGameTime, setLibraryGameTime] = useState('');
  const [libraryGameCategory, setLibraryGameCategory] = useState('');
  const [libraryError, setLibraryError] = useState('');
  const [addingFromLibrary, setAddingFromLibrary] = useState(false);
  const [rankingMethod, setRankingMethod] = useState(competition?.rankingMethod || 'byCategory');
  const [fixingPlayingGames, setFixingPlayingGames] = useState(false);
  const [fixPlayingGamesMessage, setFixPlayingGamesMessage] = useState('');
  const [deletingCompetition, setDeletingCompetition] = useState(false);

  // Check if competition can be deleted (only 1 active player)
  const activePlayers = players.filter(p => p.status === 'active');
  const canDeleteCompetition = activePlayers.length <= 1;

  if (!isOrganizer) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-red-600">Access denied. Only organizers can view this page.</p>
      </div>
    );
  }

  const handleOpenCategoryModal = async (category = null) => {
    if (category) {
      // Edit mode
      setEditingCategory(category);
      setCategoryName(category.name);
      setCategoryIcon(category.icon || '🎲');
      setCategoryColor(category.color || '#3B82F6');
      setCategoryMode('new'); // Edit mode always uses the form
    } else {
      // Create mode
      setEditingCategory(null);
      setCategoryName('');
      setCategoryIcon('🎲');
      setCategoryColor('#3B82F6');
      setCategoryMode('new');
      setSelectedExistingCategory('');

      // Load existing categories from league if competition is linked to a league
      console.log('=== Add Category Debug ===');
      console.log('competition.leagueId:', competition.leagueId);
      if (competition.leagueId) {
        setLoadingExistingCategories(true);
        try {
          const leagueCategories = await getUniqueCategoriesForLeague(competition.leagueId);
          console.log('League categories found:', leagueCategories);
          // Filter out categories that already exist in this competition
          const existingCatNames = categories.filter(c => c.status === 'active').map(c => c.name.toLowerCase());
          console.log('Existing category names in competition:', existingCatNames);
          const availableCategories = leagueCategories.filter(
            lc => !existingCatNames.includes(lc.name.toLowerCase())
          );
          console.log('Available categories (after filtering):', availableCategories);
          setExistingCategories(availableCategories);
        } catch (err) {
          console.error('Error loading league categories:', err);
          setExistingCategories([]);
        } finally {
          setLoadingExistingCategories(false);
        }
      } else {
        console.log('No leagueId on competition - skipping library lookup');
        setExistingCategories([]);
      }
    }
    setCategoryError('');
    setShowCategoryModal(true);
  };

  const handleCloseCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCategory(null);
    setCategoryName('');
    setCategoryIcon('🎲');
    setCategoryColor('#3B82F6');
    setCategoryError('');
    setCategoryMode('new');
    setSelectedExistingCategory('');
    setExistingCategories([]);
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    setCategoryError('');
    setCategorySaving(true);

    try {
      if (editingCategory) {
        // Update existing category
        await updateCategory(editingCategory.id, {
          name: categoryName,
          icon: categoryIcon,
          color: categoryColor
        });
        setSuccess('Category updated successfully');
      } else if (categoryMode === 'existing' && selectedExistingCategory) {
        // Create from existing league category
        const selectedCat = existingCategories.find(c => c.name === selectedExistingCategory);
        if (!selectedCat) {
          throw new Error('Selected category not found');
        }
        const order = categories.length;
        await createCategory(competitionId, selectedCat.name, order, categoryIcon, categoryColor);
        setSuccess('Category added successfully');
      } else {
        // Create new category
        if (!categoryName.trim()) {
          throw new Error('Category name is required');
        }
        const order = categories.length;
        await createCategory(competitionId, categoryName, order, categoryIcon, categoryColor);
        setSuccess('Category created successfully');
      }

      await refreshCompetition();
      handleCloseCategoryModal();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setCategoryError(err.message);
    } finally {
      setCategorySaving(false);
    }
  };

  const handleCreateGame = async (e) => {
    e.preventDefault();
    setGameError('');
    setGameSaving(true);

    try {
      await createGame({
        competitionId,
        categoryId: gameCategory,
        name: gameName,
        date: gameDate,
        startTime: gameTime || null,
        notes: gameNotes || null,
        knockoutMode: gameKnockoutMode
      });
      await refreshCompetition();
      setGameName('');
      setGameCategory('');
      setGameDate(getTodayDateString());
      setGameTime('');
      setGameNotes('');
      setGameKnockoutMode(false);
      setShowGameModal(false);
      setSuccess('Game created successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setGameError(err.message);
    } finally {
      setGameSaving(false);
    }
  };

  const handleRegenerateInviteCode = async () => {
    if (!window.confirm('Are you sure? This will invalidate the current invite code.')) {
      return;
    }

    try {
      const newCode = await regenerateInviteCode(competitionId);
      await refreshCompetition();
      setSuccess(`New invite code: ${newCode}`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      alert('Error regenerating code: ' + err.message);
    }
  };

  const handleChangeCompetitionStatus = async (newStatus) => {
    if (!window.confirm(`Change competition status to "${newStatus}"?`)) {
      return;
    }

    try {
      await updateCompetitionStatus(competitionId, newStatus);
      await refreshCompetition();
      setSuccess(`Competition status changed to ${newStatus}`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      alert('Error changing status: ' + err.message);
    }
  };

  const handleDeactivatePlayer = async (playerId, playerName) => {
    if (!window.confirm(`Deactivate player "${playerName}"? They will no longer appear in leaderboards.`)) {
      return;
    }

    try {
      await updatePlayerStatus(playerId, 'inactive');
      await refreshCompetition();
      setSuccess('Player deactivated');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      alert('Error deactivating player: ' + err.message);
    }
  };

  const handleResetPlayerPin = async (playerId, playerName) => {
    const pin = generatePin(6);

    if (!window.confirm(`Reset PIN for "${playerName}"? New PIN will be: ${pin}`)) {
      return;
    }

    try {
      await resetPlayerPin(playerId, pin);
      setSelectedPlayer({ name: playerName, pin });
      setShowPlayerModal(true);
    } catch (err) {
      alert('Error resetting PIN: ' + err.message);
    }
  };

  const handleShowDebugInfo = async () => {
    setLoadingDebug(true);
    setShowDebugModal(true);

    try {
      const tokens = await getCompetitionTokens(competitionId);

      // Enrich token data with player names
      const enrichedTokens = tokens.map(token => {
        const player = players.find(p => p.id === token.playerId);
        return {
          ...token,
          playerName: player?.displayName || 'Unknown Player',
          playerStatus: player?.status || 'unknown'
        };
      });

      setDebugTokens(enrichedTokens);
    } catch (err) {
      alert('Error loading debug info: ' + err.message);
    } finally {
      setLoadingDebug(false);
    }
  };

  const handleDeletePendingGame = async (gameId, gameName) => {
    if (!window.confirm(`Are you sure you want to delete "${gameName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await deletePendingGame(gameId);
      setSuccess('Game deleted successfully');
      await refreshCompetition();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting game:', err);
      alert('Failed to delete game: ' + err.message);
    }
  };

  const handleUpdateRankingMethod = async () => {
    try {
      await updateCompetition(competitionId, { rankingMethod });
      await refreshCompetition();
      setEditingRankingMethod(false);
      setSuccess('Ranking method updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating ranking method:', err);
      alert('Failed to update ranking method: ' + err.message);
    }
  };

  const handleCancelRankingMethodEdit = () => {
    setRankingMethod(competition.rankingMethod || 'byCategory');
    setEditingRankingMethod(false);
  };

  const handleFixDatabase = async () => {
    if (!window.confirm('This will add the rankingMethod field to your competition in the database. Continue?')) {
      return;
    }

    try {
      const updated = await addRankingMethodToCompetition(competitionId);
      if (updated) {
        setSuccess('Database updated! The rankingMethod field has been added. Refreshing...');
        setTimeout(async () => {
          await refreshCompetition();
          window.location.reload();
        }, 1500);
      } else {
        setSuccess('Database already has rankingMethod field. Refreshing page...');
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (err) {
      console.error('Error fixing database:', err);
      alert('Failed to update database: ' + err.message);
    }
  };

  const handleFixPlayingGames = async () => {
    setFixingPlayingGames(true);
    setFixPlayingGamesMessage('');

    try {
      const updatedCount = await fixPlayingGamesStatus(competitionId);
      setFixPlayingGamesMessage(`✓ Fixed ${updatedCount} game${updatedCount !== 1 ? 's' : ''} to "playing" status`);

      if (updatedCount > 0) {
        setTimeout(async () => {
          await refreshCompetition();
          setFixPlayingGamesMessage('');
        }, 3000);
      }
    } catch (err) {
      console.error('Error fixing playing games:', err);
      setFixPlayingGamesMessage('Failed to fix playing games: ' + err.message);
    } finally {
      setFixingPlayingGames(false);
    }
  };

  const handleOpenLibrary = async () => {
    if (!competition.leagueId) {
      alert('This competition is not linked to a league. Only league competitions can use the game library.');
      return;
    }

    setShowLibraryModal(true);
    setLoadingLibrary(true);
    setLibraryError('');
    setSelectedLibraryGame(null);
    setLibraryGameDate(getTodayDateString());
    setLibraryGameTime('');
    setLibraryGameCategory('');

    try {
      const games = await getGameSectionsForLeague(competition.leagueId);
      setLibraryGames(games);
    } catch (err) {
      console.error('Error loading library:', err);
      setLibraryError('Failed to load game library');
    } finally {
      setLoadingLibrary(false);
    }
  };

  const handleSelectLibraryGame = (game) => {
    setSelectedLibraryGame(game);
    // Try to find a matching category by name
    const matchingCategory = categories.find(
      c => c.status === 'active' && c.name.toLowerCase() === game.categoryName?.toLowerCase()
    );
    if (matchingCategory) {
      setLibraryGameCategory(matchingCategory.id);
    } else {
      setLibraryGameCategory('');
    }
  };

  const handleAddFromLibrary = async () => {
    if (!selectedLibraryGame) {
      setLibraryError('Please select a game');
      return;
    }
    if (!libraryGameCategory) {
      setLibraryError('Please select a category');
      return;
    }

    setAddingFromLibrary(true);
    setLibraryError('');

    try {
      await createGame({
        competitionId,
        categoryId: libraryGameCategory,
        name: selectedLibraryGame.name,
        date: libraryGameDate,
        startTime: libraryGameTime || null,
        notes: null,
        knockoutMode: false,
        gameSectionId: selectedLibraryGame.id // Link to library game for duration tracking
      });
      await refreshCompetition();
      setShowLibraryModal(false);
      setSelectedLibraryGame(null);
      setSuccess('Game added from library');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error adding game from library:', err);
      setLibraryError('Failed to add game: ' + err.message);
    } finally {
      setAddingFromLibrary(false);
    }
  };

  const handleDeleteCompetition = async () => {
    if (!canDeleteCompetition) {
      alert('Cannot delete competition with multiple active players.');
      return;
    }

    const confirmMessage = `Are you sure you want to permanently delete "${competition.name}"?\n\nThis will delete:\n- All players\n- All categories\n- All games\n- All results\n\nThis action CANNOT be undone!`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    // Double confirmation for safety
    const typedName = window.prompt(`Type the competition name "${competition.name}" to confirm deletion:`);
    if (typedName !== competition.name) {
      alert('Competition name did not match. Deletion cancelled.');
      return;
    }

    setDeletingCompetition(true);

    try {
      await deleteCompetition(competitionId);

      // Redirect to home or league page
      if (competition.leagueId) {
        navigate(`/league/${competition.leagueId}`);
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Error deleting competition:', err);
      alert('Failed to delete competition: ' + err.message);
      setDeletingCompetition(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <HamburgerMenu />
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <h1 className="text-3xl font-bold text-gray-900">Manage Competition</h1>
          <p className="text-sm text-gray-600 mt-1">{competition.name}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded text-green-700">
            {success}
          </div>
        )}

        {/* Competition Status */}
        <Card title="Competition Status" className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Current Status:</p>
              <p className="text-lg font-bold capitalize">{competition.status}</p>
            </div>
            <div className="flex gap-2">
              {competition.status === 'draft' && (
                <Button size="sm" onClick={() => handleChangeCompetitionStatus('live')}>
                  Start Competition
                </Button>
              )}
              {competition.status === 'live' && (
                <Button size="sm" onClick={() => handleChangeCompetitionStatus('finished')}>
                  Finish Competition
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Invite Code */}
        <Card title="Invite Code" className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-2">Share this code with participants:</p>
              <p className="text-3xl font-mono font-bold text-blue-600">{competition.inviteCode}</p>
            </div>
            <Button size="sm" variant="outline" onClick={handleRegenerateInviteCode}>
              Regenerate Code
            </Button>
          </div>
        </Card>

        {/* Competition Settings */}
        <Card title="Competition Settings" className="mb-6">
          <div className="space-y-4">
            {/* Fix Database Button (only show if rankingMethod doesn't exist) */}
            {!competition?.rankingMethod && (
              <div className="p-4 bg-yellow-50 border-2 border-yellow-400 rounded-lg">
                <p className="text-sm font-medium text-yellow-900 mb-2">
                  ⚠️ Database Update Required
                </p>
                <p className="text-xs text-yellow-700 mb-3">
                  Your competition was created before the ranking method feature was added. Click below to update your database.
                </p>
                <Button size="sm" onClick={handleFixDatabase}>
                  Fix Database - Add Ranking Method
                </Button>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-medium text-gray-700">Ranking Method</p>
                  <p className="text-xs text-gray-500 mt-1">How the overall leaderboard is calculated</p>
                </div>
                {!editingRankingMethod && competition?.rankingMethod && (
                  <Button size="sm" variant="outline" onClick={() => setEditingRankingMethod(true)}>
                    Edit
                  </Button>
                )}
              </div>

              {editingRankingMethod ? (
                <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <select
                    value={rankingMethod}
                    onChange={(e) => setRankingMethod(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="byCategory">By Category</option>
                    <option value="byDay">By Day</option>
                  </select>
                  <div className="text-xs text-gray-600 p-3 bg-blue-50 rounded border border-blue-200">
                    {rankingMethod === 'byCategory' ? (
                      <>
                        <strong>By Category:</strong> Players earn placement points (N..1) in each category based on their total raw points across all games in that category. The overall ranking is the sum of category placement points.
                      </>
                    ) : (
                      <>
                        <strong>By Day:</strong> For each day, players earn placement points within each category they played, then those category-day points are summed and ranked to award daily placement points. The overall ranking is the sum of daily placement points across all days.
                      </>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleUpdateRankingMethod}>
                      Save
                    </Button>
                    <Button size="sm" variant="secondary" onClick={handleCancelRankingMethodEdit}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="font-medium">
                    {(competition?.rankingMethod || 'byCategory') === 'byDay' ? 'By Day' : 'By Category'}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    {(competition?.rankingMethod || 'byCategory') === 'byDay'
                      ? 'Daily rankings based on category performance each day'
                      : 'Overall ranking based on total placement points across all categories'}
                  </p>
                </div>
              )}
            </div>

            {/* Fix Playing Games Status */}
            <div className="pt-4 mt-4 border-t border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm font-medium text-gray-700">Fix Playing Games Status</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Update games that have been started (timer clicked) to show "Playing" status
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleFixPlayingGames}
                  disabled={fixingPlayingGames}
                >
                  {fixingPlayingGames ? 'Fixing...' : 'Fix Playing Status'}
                </Button>
              </div>
              {fixPlayingGamesMessage && (
                <div className={`mt-2 p-2 rounded text-xs ${
                  fixPlayingGamesMessage.startsWith('✓')
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {fixPlayingGamesMessage}
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Categories Management */}
        <Card title="Categories" className="mb-6">
          <div className="mb-4">
            <Button onClick={() => handleOpenCategoryModal()}>Add Category</Button>
          </div>
          <div className="space-y-2">
            {categories.filter(c => c.status === 'active').map(cat => (
              <div key={cat.id} className="flex items-center justify-between p-3 rounded" style={{ backgroundColor: `${cat.color}15` }}>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{cat.icon || '🎲'}</span>
                  <span className="font-medium">{cat.name}</span>
                  <span className="text-xs text-gray-500">
                    ({games.filter(g => g.categoryId === cat.id).length} games)
                  </span>
                </div>
                <Button size="sm" variant="outline" onClick={() => handleOpenCategoryModal(cat)}>
                  Edit
                </Button>
              </div>
            ))}
            {categories.filter(c => c.status === 'active').length === 0 && (
              <p className="text-gray-500 text-center py-4">No categories yet</p>
            )}
          </div>
        </Card>

        {/* Games Management */}
        <Card title="Games" className="mb-6">
          <div className="mb-4 flex gap-2 flex-wrap">
            <Button
              onClick={() => setShowGameModal(true)}
              disabled={categories.filter(c => c.status === 'active').length === 0}
            >
              Add Game
            </Button>
            {competition.leagueId && (
              <Button
                variant="outline"
                onClick={handleOpenLibrary}
                disabled={categories.filter(c => c.status === 'active').length === 0}
              >
                Add from Library
              </Button>
            )}
            {categories.filter(c => c.status === 'active').length === 0 && (
              <p className="text-sm text-gray-500 mt-2 w-full">Create categories first</p>
            )}
          </div>
          <div className="text-sm space-y-1 mb-4">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Games:</span>
              <span className="font-semibold">{games.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Played:</span>
              <span className="font-semibold">{games.filter(g => g.status === 'played').length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Planned:</span>
              <span className="font-semibold">{games.filter(g => g.status === 'planned').length}</span>
            </div>
          </div>

          {/* Pending Games List */}
          {games.filter(g => g.status === 'planned').length > 0 && (
            <>
              <div className="border-t pt-4 mt-4">
                <h4 className="font-semibold text-sm text-gray-700 mb-3">Pending Games</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {games
                    .filter(g => g.status === 'planned')
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map(game => {
                      const category = categories.find(c => c.id === game.categoryId);
                      return (
                        <div
                          key={game.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{game.name}</div>
                            <div className="text-xs text-gray-500">
                              {category?.icon} {category?.name} • {game.date}
                              {game.startTime && ` at ${game.startTime}`}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleDeletePendingGame(game.id, game.name)}
                          >
                            Delete
                          </Button>
                        </div>
                      );
                    })}
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Player Management */}
        <Card title="Players">
          <div className="mb-4">
            <Button size="sm" variant="outline" onClick={handleShowDebugInfo}>
              Debug: View All Tokens
            </Button>
          </div>
          <div className="space-y-2">
            {players.filter(p => p.status === 'active').map(player => (
              <div key={player.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <span className="font-medium">{player.displayName}</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => handleResetPlayerPin(player.id, player.displayName)}>
                    Reset PIN
                  </Button>
                  <Button size="sm" variant="danger" onClick={() => handleDeactivatePlayer(player.id, player.displayName)}>
                    Deactivate
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Danger Zone */}
        <Card className="mt-6 border-2 border-red-200">
          <h3 className="text-lg font-bold text-red-600 mb-4">Danger Zone</h3>
          <div className="p-4 bg-red-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Delete Competition</p>
                <p className="text-sm text-gray-600 mt-1">
                  {canDeleteCompetition
                    ? 'Permanently delete this competition and all its data.'
                    : `Cannot delete - there are ${activePlayers.length} active players. Only competitions with 1 player can be deleted.`}
                </p>
              </div>
              <Button
                variant="danger"
                onClick={handleDeleteCompetition}
                disabled={!canDeleteCompetition || deletingCompetition}
              >
                {deletingCompetition ? 'Deleting...' : 'Delete Competition'}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Create/Edit Category Modal */}
      <Modal
        isOpen={showCategoryModal}
        onClose={handleCloseCategoryModal}
        title={editingCategory ? 'Edit Category' : 'Add Category'}
      >
        <form onSubmit={handleSaveCategory}>
          {/* Mode selector - only show when creating and league has categories */}
          {!editingCategory && existingCategories.length > 0 && (
            <div className="mb-4">
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setCategoryMode('existing')}
                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg border-2 transition-all ${
                    categoryMode === 'existing'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  From Library
                </button>
                <button
                  type="button"
                  onClick={() => setCategoryMode('new')}
                  className={`flex-1 py-2 px-3 text-sm font-medium rounded-lg border-2 transition-all ${
                    categoryMode === 'new'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                  }`}
                >
                  Create New
                </button>
              </div>
            </div>
          )}

          {/* Loading state */}
          {loadingExistingCategories && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-sm text-gray-500">Loading categories...</p>
            </div>
          )}

          {/* Existing category dropdown */}
          {!editingCategory && categoryMode === 'existing' && existingCategories.length > 0 && !loadingExistingCategories && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Category
              </label>
              <select
                value={selectedExistingCategory}
                onChange={(e) => setSelectedExistingCategory(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                required={categoryMode === 'existing'}
              >
                <option value="">Choose a category...</option>
                {existingCategories.map(cat => (
                  <option key={cat.name} value={cat.name}>{cat.name}</option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Categories from your league's game library
              </p>
            </div>
          )}

          {/* New category input - show when creating new or editing */}
          {(editingCategory || categoryMode === 'new') && !loadingExistingCategories && (
            <Input
              label="Category Name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
              placeholder="e.g., Poker, Board Games"
              required={categoryMode === 'new' || editingCategory}
              error={categoryError}
            />
          )}

          {!loadingExistingCategories && (
            <>
              <IconPicker value={categoryIcon} onChange={setCategoryIcon} />

              <ColorPicker value={categoryColor} onChange={setCategoryColor} />

              <div className="flex gap-2 mt-4">
                <Button
                  type="submit"
                  disabled={categorySaving || (categoryMode === 'existing' && !selectedExistingCategory) || (categoryMode === 'new' && !editingCategory && !categoryName.trim())}
                  fullWidth
                >
                  {categorySaving
                    ? (editingCategory ? 'Updating...' : 'Adding...')
                    : (editingCategory ? 'Update Category' : 'Add Category')}
                </Button>
                <Button type="button" variant="outline" onClick={handleCloseCategoryModal}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </form>
      </Modal>

      {/* Create Game Modal */}
      <Modal
        isOpen={showGameModal}
        onClose={() => setShowGameModal(false)}
        title="Add Game"
        size="lg"
      >
        <form onSubmit={handleCreateGame}>
          <Input
            label="Game Name"
            value={gameName}
            onChange={(e) => setGameName(e.target.value)}
            placeholder="e.g., Texas Hold'em"
            required
          />

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={gameCategory}
              onChange={(e) => setGameCategory(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a category</option>
              {categories.filter(c => c.status === 'active').map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <Input
            label="Date"
            type="date"
            value={gameDate}
            onChange={(e) => setGameDate(e.target.value)}
            required
          />

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Time (Optional)
            </label>
            <select
              value={gameTime}
              onChange={(e) => setGameTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">No specific time</option>
              {generateTimeOptions().map(time => (
                <option key={time} value={time}>{time}</option>
              ))}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
            <textarea
              value={gameNotes}
              onChange={(e) => setGameNotes(e.target.value)}
              placeholder="Any additional notes..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={gameKnockoutMode}
                onChange={(e) => setGameKnockoutMode(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Knockout Mode
              </span>
            </label>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              Players are eliminated one by one. Points awarded based on elimination order (1st out = 1 point, 2nd out = 2 points, etc.)
            </p>
          </div>

          {gameError && <p className="text-sm text-red-500 mb-4">{gameError}</p>}

          <div className="flex gap-2">
            <Button type="submit" disabled={gameSaving} fullWidth>
              {gameSaving ? 'Adding...' : 'Add Game'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowGameModal(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>

      {/* Player PIN Reset Modal */}
      <Modal
        isOpen={showPlayerModal}
        onClose={() => setShowPlayerModal(false)}
        title="PIN Reset Successful"
      >
        {selectedPlayer && (
          <div>
            <p className="text-sm text-gray-700 mb-4">
              New PIN for <span className="font-semibold">{selectedPlayer.name}</span>:
            </p>
            <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 mb-4">
              <p className="text-3xl font-bold text-center text-gray-900 tracking-widest">
                {selectedPlayer.pin}
              </p>
            </div>
            <p className="text-sm text-gray-600">
              Share this PIN with the player. They will need it to access the competition from a new device.
            </p>
            <Button onClick={() => setShowPlayerModal(false)} fullWidth className="mt-4">
              Close
            </Button>
          </div>
        )}
      </Modal>

      {/* Debug Tokens Modal */}
      <Modal
        isOpen={showDebugModal}
        onClose={() => setShowDebugModal(false)}
        title="Debug: Competition Tokens"
        size="lg"
      >
        <div>
          <p className="text-sm text-gray-600 mb-4">
            This shows all access tokens for this competition. Use this to diagnose issues when users can't access the competition.
          </p>

          {loadingDebug ? (
            <p className="text-center text-gray-500 py-8">Loading tokens...</p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {debugTokens.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No tokens found</p>
              ) : (
                debugTokens.map(token => (
                  <div key={token.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="font-semibold text-gray-700">Player:</span>
                        <p className="text-gray-900">{token.playerName}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-700">Status:</span>
                        <p className={`${token.playerStatus === 'active' ? 'text-green-600' : 'text-gray-500'}`}>
                          {token.playerStatus}
                        </p>
                      </div>
                      <div className="col-span-2">
                        <span className="font-semibold text-gray-700">Auth UID:</span>
                        <p className="font-mono text-gray-900 break-all">{token.authUid}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="font-semibold text-gray-700">Token ID:</span>
                        <p className="font-mono text-gray-600 break-all text-[10px]">{token.id}</p>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-700">Created:</span>
                        <p className="text-gray-900">
                          {token.createdAt?.toDate?.() ? new Date(token.createdAt.toDate()).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="font-semibold text-gray-700">Last Seen:</span>
                        <p className="text-gray-900">
                          {token.lastSeenAt?.toDate?.() ? new Date(token.lastSeenAt.toDate()).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      {token.deviceLabel && (
                        <div className="col-span-2">
                          <span className="font-semibold text-gray-700">Device:</span>
                          <p className="text-gray-600 text-[10px] truncate">{token.deviceLabel}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
            <p className="font-semibold mb-1">Common Issues:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Multiple Auth UIDs for same player = User linked email after joining anonymously</li>
              <li>Invalid invite code = User's current Auth UID doesn't match any token</li>
              <li>Solution: Reset the player's PIN and have them rejoin with the new PIN</li>
            </ul>
          </div>

          <Button onClick={() => setShowDebugModal(false)} fullWidth className="mt-4">
            Close
          </Button>
        </div>
      </Modal>

      {/* Add from Library Modal */}
      <Modal
        isOpen={showLibraryModal}
        onClose={() => setShowLibraryModal(false)}
        title="Add Game from Library"
        size="lg"
      >
        <div>
          {loadingLibrary ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-gray-600">Loading library...</p>
            </div>
          ) : libraryGames.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-6xl mb-4">📚</p>
              <p className="text-gray-600 mb-4">No games in the league library yet.</p>
              <p className="text-sm text-gray-500">
                Add games to the library from the league dashboard.
              </p>
            </div>
          ) : (
            <>
              {/* Game Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select a Game
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto p-1">
                  {libraryGames.map(game => (
                    <div
                      key={game.id}
                      onClick={() => handleSelectLibraryGame(game)}
                      className={`p-2 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedLibraryGame?.id === game.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="aspect-square bg-gray-100 rounded mb-1 flex items-center justify-center overflow-hidden">
                        {game.photoUrl ? (
                          <img src={game.photoUrl} alt={game.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-2xl opacity-30">🎲</span>
                        )}
                      </div>
                      <p className="text-xs font-medium truncate">{game.name}</p>
                      <p className="text-[10px] text-gray-500 truncate">{game.categoryName}</p>
                      {game.averageDurationMinutes && (
                        <p className="text-[10px] text-gray-400">~{game.averageDurationMinutes} min</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Scheduling */}
              {selectedLibraryGame && (
                <div className="border-t pt-4 space-y-4">
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="font-medium">{selectedLibraryGame.name}</p>
                    <p className="text-sm text-gray-600">{selectedLibraryGame.categoryName}</p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={libraryGameCategory}
                      onChange={(e) => setLibraryGameCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a category</option>
                      {categories.filter(c => c.status === 'active').map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                      ))}
                    </select>
                  </div>

                  <Input
                    label="Date"
                    type="date"
                    value={libraryGameDate}
                    onChange={(e) => setLibraryGameDate(e.target.value)}
                    required
                  />

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Time (Optional)
                    </label>
                    <select
                      value={libraryGameTime}
                      onChange={(e) => setLibraryGameTime(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">No specific time</option>
                      {generateTimeOptions().map(time => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {libraryError && (
                <p className="text-sm text-red-500 mt-2">{libraryError}</p>
              )}

              <div className="flex gap-2 mt-4">
                <Button
                  onClick={handleAddFromLibrary}
                  disabled={!selectedLibraryGame || !libraryGameCategory || addingFromLibrary}
                  fullWidth
                >
                  {addingFromLibrary ? 'Adding...' : 'Add to Schedule'}
                </Button>
                <Button variant="outline" onClick={() => setShowLibraryModal(false)}>
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanel;
